import React from "react"
import dayjs from "dayjs"
import {
   PropertyType,
   BaseProperty,
   regionTextByCode,
   RegionCode,
} from "@casas-diamante/types"
import {Box, Chip} from "@mui/material"
import {CDBLUE, IMAGES_URL, REDIRECT_URL} from "./constants"
import type {SerializedFirestoreTimestamp} from "@/types/Property"

const zoneLabelByValue: Record<string, string> = {
   "Acapulco Playa Diamante": "Playa Diamante",
   "Acapulco Dorado": "Dorada (Bahía)",
}

export const displayZoneLabel = (zone: string) => zoneLabelByValue[zone] ?? zone

export type PropertyScheme = "sale" | "rent"

const PRICE_ANALYSIS_START_DATE = dayjs("2024-03-01")

/** Returns the first date covered by the price-analysis activity notice. */
export const getPriceAnalysisStartDate = (
   createdAt?: SerializedFirestoreTimestamp | null,
): string => {
   if (!createdAt || !Number.isFinite(createdAt._seconds)) {
      return PRICE_ANALYSIS_START_DATE.format("DD/MM/YYYY")
   }

   const creationDate = dayjs.unix(createdAt._seconds)
   if (!creationDate.isValid() || !creationDate.isAfter(PRICE_ANALYSIS_START_DATE, "day")) {
      return PRICE_ANALYSIS_START_DATE.format("DD/MM/YYYY")
   }

   return creationDate.format("DD/MM/YYYY")
}

/** Utility function to get the image URL from a path */
export const getImageURL = (path: string) => `${IMAGES_URL}/${path}`

/** Order image paths based on orderedSections property */
export const sortImagePaths = (
   images: BaseProperty["images"],
   orderedSections: BaseProperty["orderedSections"],
) => {
   const orderedImages = {} as Record<string, string[]>
   for (const section of orderedSections) {
      orderedImages[section] = images[section]
   }
   return orderedImages
}

export function standarizeName(name: string) {
   const normalized = name
      .normalize("NFD") // Normalize to decomposed form
      .replace(/[\u0300-\u036f]/g, "") // Remove diacritic marks (tildes and such)
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")

   return normalized
}

export const getTypeForURL = (type: string) => {
   return type === PropertyType.House
      ? "casas"
      : type === PropertyType.Special
      ? "especiales"
      : type === PropertyType.Apartment
      ? "departamentos"
      : type === PropertyType.Commercial
      ? "locales-oficinas"
      : type === PropertyType.Special
      ? "especial"
      : "terrenos"
}

export const isMobile = () => {
   return window.innerWidth <= 768
}

type CacheKey = "fetchedPartners" | "properties" | "condos" | "partners"

/**
 * Clears whatever we have stored in localStorage as cache.
 */
export const clearCache = (keys?: CacheKey[]) => {
   if (!keys) {
      keys = ["fetchedPartners", "properties", "condos"]
   }
   keys.forEach((key) => {
      localStorage.removeItem(key)
   })
}

export const getPathUrl = (
   region: RegionCode,
   type: string,
   scheme: PropertyScheme,
   slug: string,
) => {
   return `/${regionTextByCode[region]?.toLowerCase() ?? "acapulco"}/${getTypeForURL(type)}-en-${
      scheme === "rent" ? "renta" : "venta"
   }/${slug}`
}

export const getShareUrl = (
   region: RegionCode,
   type: string,
   scheme: PropertyScheme,
   slug: string,
) => {
   const path = getPathUrl(region, type, scheme, slug)

   return `${REDIRECT_URL}${path}`
}

/** Parses stored filter values into a clean string array. */
export const parseStoredStringArray = (value: unknown): string[] => {
   if (Array.isArray(value)) return value.map(String).filter(Boolean)
   if (typeof value !== "string" || !value) return []

   try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
      if (typeof parsed === "string") return parsed ? [parsed] : []
   } catch {
      return value
         .split(",")
         .map((item) => item.trim())
         .filter(Boolean)
   }

   return []
}

export const renderSelectedFilterValues = (selected: string[]) => {
   const displayOptions = selected.slice(0, 1)
   const remainingCount = selected.length - displayOptions.length

   return React.createElement(
      Box,
      {
         sx: {
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            alignItems: "center",
            maxWidth: "100%",
            overflow: "hidden",
         },
      },
      ...displayOptions.map((value) =>
         React.createElement(Chip, {
            key: value,
            label: value,
            sx: {
               bgcolor: CDBLUE,
               color: "white",
               maxWidth: remainingCount > 0 ? 160 : 210,
               minWidth: 0,
               "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
               },
            },
         }),
      ),
      remainingCount > 0
         ? React.createElement(Chip, {
              key: "remaining-count",
              label: `+${remainingCount}`,
              sx: {
                  bgcolor: CDBLUE,
                  color: "white",
                  flexShrink: 0,
              },
           })
         : null,
   )
}
